// ============================================
// SectionList — UKA-X01 D5
//
// Filterable list of ClinicalReviewItem entries.
// Each row: title, ContentType badge, status chip, last-updated timestamp.
// 48px min touch target. Sorted by item.order.
// React.memo on list + row prevents jank during active review sessions
// (207 onSnapshot re-renders when verdicts land).
// ============================================

import React, { memo } from 'react';
import type { ContentStatus, ContentType } from '../../data/portal/schema';
import type { ClinicalReviewItemWithId } from '../hooks/useReviewContent';

export interface SectionListProps {
  sections: ClinicalReviewItemWithId[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

// ── Status chip config ────────────────────────────────────────────────────────

type StatusConfig = {
  label: string;
  bg: string;
  color: string;
  lineThrough?: boolean;
};

const STATUS_CONFIG: Record<ContentStatus, StatusConfig> = {
  IN_REVIEW:                { label: 'In Review',          bg: '#F4F4F5', color: '#71717A' },
  STAGED:                   { label: 'Staged',              bg: '#F4F4F5', color: '#71717A' },
  APPROVED:                 { label: 'Approved',            bg: '#DCFCE7', color: '#15803D' },
  APPROVED_WITH_CONDITIONS: { label: 'Approved w/ Cond.',   bg: '#DCFCE7', color: '#15803D' },
  CHANGE_REQUESTED:         { label: 'Change Requested',    bg: '#FEF9C3', color: '#A16207' },
  REJECTED:                 { label: 'Rejected',            bg: '#FEE2E2', color: '#B91C1C' },
  WITHDRAWN:                { label: 'Withdrawn',           bg: '#F4F4F5', color: '#A1A1AA', lineThrough: true },
  BLOCKED:                  { label: 'Blocked',             bg: '#F4F4F5', color: '#A1A1AA' },
};

// ── ContentType human labels ──────────────────────────────────────────────────

const TYPE_LABEL: Record<ContentType, string> = {
  formulary_supplement: 'Supplement',
  formulary_rx:         'Rx',
  formulary_longevity:  'Longevity',
  biomarker_range:      'Biomarker',
  evidence_brief:       'Evidence',
  range_decision:       'Range Decision',
  gap_question:         'Gap Q',
  methodology:          'Methodology',
  attestation:          'Attestation',
  protocol_rule:        'Protocol',
};

// ── Timestamp formatter ───────────────────────────────────────────────────────

// Accepts Firestore Timestamp shape (toDate()) or raw seconds
function formatTimestamp(
  ts: { toDate?: () => Date; seconds?: number } | null | undefined,
): string {
  if (!ts) return '—';
  try {
    const date =
      typeof ts.toDate === 'function'
        ? ts.toDate()
        : new Date((ts.seconds ?? 0) * 1000);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

// ── SectionRow ────────────────────────────────────────────────────────────────

const SectionRow = memo(function SectionRow({
  item,
  isSelected,
  onSelect,
}: {
  item: ClinicalReviewItemWithId;
  isSelected: boolean;
  onSelect: (id: string) => void;
}) {
  const statusCfg: StatusConfig = STATUS_CONFIG[item.status] ?? STATUS_CONFIG['IN_REVIEW'];
  const typeLabel = TYPE_LABEL[item.type] ?? item.type;
  // Show lockedAt for terminal states, else stagedAt
  const timestamp = item.lockedAt
    ? formatTimestamp(item.lockedAt as Parameters<typeof formatTimestamp>[0])
    : formatTimestamp(item.stagedAt as Parameters<typeof formatTimestamp>[0]);

  return (
    <button
      role="option"
      aria-selected={isSelected}
      aria-label={`${item.title} — ${statusCfg.label}`}
      onClick={() => onSelect(item.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        minHeight: '48px',
        padding: '10px 16px',
        textAlign: 'left',
        backgroundColor: isSelected ? '#FFF1F2' : '#FFFFFF',
        border: 'none',
        borderBottom: '1px solid #F4F4F5',
        borderLeft: isSelected ? '3px solid #E11D48' : '3px solid transparent',
        cursor: 'pointer',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        transition: 'background-color 0.15s ease, border-left-color 0.15s ease',
      }}
    >
      {/* Title + sub-row */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '16px',
            fontWeight: isSelected ? 600 : 400,
            color: '#18181B',
            textDecoration: statusCfg.lineThrough ? 'line-through' : 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '3px',
          }}
        >
          {/* ContentType badge */}
          <span
            style={{
              fontSize: '13px',
              backgroundColor: '#F4F4F5',
              color: '#71717A',
              padding: '1px 7px',
              borderRadius: '4px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {typeLabel}
          </span>
          {/* Timestamp */}
          <span style={{ fontSize: '13px', color: '#A1A1AA' }}>{timestamp}</span>
          {/* Flags */}
          {item.offLabel && (
            <span
              style={{
                fontSize: '11px',
                backgroundColor: '#FEF3C7',
                color: '#92400E',
                padding: '1px 6px',
                borderRadius: '4px',
                fontWeight: 600,
              }}
            >
              Off-label
            </span>
          )}
          {item.controversial && (
            <span
              style={{
                fontSize: '11px',
                backgroundColor: '#FEE2E2',
                color: '#991B1B',
                padding: '1px 6px',
                borderRadius: '4px',
                fontWeight: 600,
              }}
            >
              Controversial
            </span>
          )}
        </div>
      </div>

      {/* Status chip */}
      <span
        style={{
          fontSize: '13px',
          backgroundColor: statusCfg.bg,
          color: statusCfg.color,
          padding: '3px 9px',
          borderRadius: '4px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {statusCfg.label}
      </span>
    </button>
  );
});

// ── SectionList ───────────────────────────────────────────────────────────────

export const SectionList = memo(function SectionList({
  sections,
  selectedId,
  onSelect,
}: SectionListProps) {
  const sorted = [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (sorted.length === 0) {
    return (
      <div
        style={{
          padding: '32px 16px',
          textAlign: 'center',
          fontSize: '16px',
          color: '#A1A1AA',
          fontFamily: 'DM Sans, system-ui, sans-serif',
        }}
      >
        No sections in this category.
      </div>
    );
  }

  return (
    <div role="listbox" aria-label="Review sections">
      {sorted.map((item) => (
        <SectionRow
          key={item.id}
          item={item}
          isSelected={item.id === selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
});
